import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	BottomNavigation,
	BottomNavigationAction,
	ThemeProvider,
} from '@mui/material';

import { useFeatureFlag } from '@mopla/business-logic';
import { VAR_AREA_BOT } from '@mopla/constants';
import { passengerThemeV2 } from '@mopla/mui';

import ActivitiesFilled from '../../icons/ActivitiesFilled.svg';
import ActivitiesOutlined from '../../icons/ActivitiesOutlined.svg';
import HomeFilledIcon from '../../icons/HomeFilledIcon.svg';
import HomeOutlinedIcon from '../../icons/HomeOutlinedIcon.svg';
import ProfileFilledIcon from '../../icons/ProfileFilledIcon.svg';
import ProfileOutlined from '../../icons/ProfileOutlined.svg';
import SearchFilledIcon from '../../icons/SearchFilledIcon.svg';
import SearchOutlinedIcon from '../../icons/SearchOutlinedIcon.svg';
import TicketFilledIcon from '../../icons/TicketFilledIcon.svg';
import TicketOutlinedIcon from '../../icons/TicketOutlinedIcon.svg';

export enum EPassengerNavTab {
	Home = 'home',
	Search = 'search',
	Profile = 'profile',
	Tickets = 'tickets',
	Activities = 'activities',
}

const HOME_ITEM = {
	id: EPassengerNavTab.Home,
	value: '/home',
	labelKey: 'start',
	filled: HomeFilledIcon,
	outlined: HomeOutlinedIcon,
	dataTestId: 'passengerBottomNavigation-start-button',
};

const SEARCH_ITEM = {
	id: EPassengerNavTab.Search,
	value: '/home/search',
	labelKey: 'search',
	filled: SearchFilledIcon,
	outlined: SearchOutlinedIcon,
	dataTestId: 'passengerBottomNavigation-search-button',
};

const PROFILE_ITEM = {
	id: EPassengerNavTab.Profile,
	value: '/home/profile',
	labelKey: 'profile',
	filled: ProfileFilledIcon,
	outlined: ProfileOutlined,
	dataTestId: 'passengerBottomNavigation-profile-button',
};

const TICKETS_ITEM = {
	id: EPassengerNavTab.Tickets,
	value: '/home/tickets',
	labelKey: 'tickets',
	filled: TicketFilledIcon,
	outlined: TicketOutlinedIcon,
	dataTestId: 'passengerBottomNavigation-tickets-button',
};

const ACTIVITIES_ITEM = {
	id: EPassengerNavTab.Activities,
	value: '/home/activities',
	labelKey: 'activities',
	filled: ActivitiesFilled,
	outlined: ActivitiesOutlined,
	dataTestId: 'passengerBottomNavigation-activities-button',
};

/** Activities replaces the Tickets tab and moves behind Search */
const ACTIVITIES_NAVIGATION_ITEMS = [
	HOME_ITEM,
	SEARCH_ITEM,
	ACTIVITIES_ITEM,
	PROFILE_ITEM,
];

const TICKETS_NAVIGATION_ITEMS = [
	HOME_ITEM,
	TICKETS_ITEM,
	SEARCH_ITEM,
	PROFILE_ITEM,
];

interface IPassengerBottomNavigationProps {
	onTabClick?: (tab: EPassengerNavTab) => void;
}

const PassengerBottomNavigation: FC<IPassengerBottomNavigationProps> = ({
	onTabClick,
}) => {
	const { t } = useTranslation('navigation');
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { isNewActivitiesEnabled } = useFeatureFlag();
	const [value, setValue] = useState('/home');

	const navigationItems = isNewActivitiesEnabled()
		? ACTIVITIES_NAVIGATION_ITEMS
		: TICKETS_NAVIGATION_ITEMS;

	useEffect(() => {
		setValue(pathname);
	}, [pathname]);

	return (
		<ThemeProvider theme={passengerThemeV2}>
			<BottomNavigation
				sx={{
					pb: VAR_AREA_BOT,
					mb: `calc(-1 * ${VAR_AREA_BOT})`,
				}}
				showLabels
				value={value}
				onChange={(_, newValue) => {
					navigate(newValue);
				}}
			>
				{navigationItems.map(
					({
						id,
						value: itemValue,
						labelKey,
						filled: Filled,
						outlined: Outlined,
						dataTestId,
					}) => (
						<BottomNavigationAction
							key={itemValue}
							value={itemValue}
							label={t(labelKey)}
							icon={value === itemValue ? <Filled /> : <Outlined />}
							data-testid={dataTestId}
							onClick={() => onTabClick?.(id)}
						/>
					)
				)}
			</BottomNavigation>
		</ThemeProvider>
	);
};

export default PassengerBottomNavigation;
